import { create } from 'zustand';
/**
 * PART 9: EXECUTION ENGINE STATE MANAGEMENT TODOs
 * ===============================================
 *
 * Future state additions:
 * - executionState: { isRunning: boolean, currentNodeId?: string, progress: number (0-100), status: 'idle'|'running'|'paused'|'completed'|'failed' }
 * - executionLogs: { nodeId: string, stepIndex: number, action: string, input: any, output: any, status: string, timestamp: Date, error?: string }[]
 * - executionMetadata: { startTime?: Date, endTime?: Date, totalDuration?: number, error?: string }
 * - triggerListeners: Map<string, { isActive: boolean, nextExecution?: Date, frequency?: string }>
 * - executionCache: { [nodeId]: { output: any, timestamp: number } } (for condition evaluation)
 *
 * Future actions:
 * - setExecutionState()
 * - addExecutionLog()
 * - clearExecutionLogs()
 * - setTriggerListeners()
 * - updateTriggerListener()
 * - resetExecution()
 */
/**
 * PART 10: ADVANCED FEATURES STATE MANAGEMENT TODOs
 * ================================================
 *
 * Future state additions:
 * - workflowTemplates: { id, name, description, canvasDefinition, category, tags }[]
 * - workflowVersions: { workflowId, history: { version: number, createdAt, canvasDefinition, publishedAt? }[] }
 * - workflowAnalytics: { workflowId, totalRuns: number, successRate: number, avgExecutionTime: number, lastRun?: Date }
 * - sharedWorkflows: { workflowId, sharedWith: { userId: string, permission: 'view'|'edit', sharedAt: Date }[] }
 * - customActions: { id, name, definition, createdBy, isPublished }[]
 * - customTriggers: { id, name, definition, createdBy, isPublished }[]
 *
 * Future actions:
 * - duplicateWorkflow()
 * - createWorkflowVersion()
 * - rollbackToVersion()
 * - shareWorkflow()
 * - getWorkflowAnalytics()
 * - createTemplateFromWorkflow()
 * - saveCustomAction()
 * - saveCustomTrigger()
 */
export const useWorkflowStore = create((set) => ({
    // State
    workflows: [],
    activeWorkflow: null,
    canvasNodes: [],
    canvasEdges: [],
    isPublished: false,
    executionHistory: [],
    canvasState: {
        zoom: 1,
        pan: { x: 0, y: 0 },
        selectedNodeId: undefined,
    },
    loading: false,
    error: null,
    // Actions
    setWorkflows: (workflows) => set({ workflows }),
    setActiveWorkflow: (workflow) => {
        set({ activeWorkflow: workflow });
        // Reset canvas when changing workflow
        if (workflow) {
            const nodes = workflow.definition?.trigger
                ? [
                    {
                        id: 'trigger',
                        type: 'trigger',
                        data: {
                            label: `When ${workflow.definition.trigger.type}`,
                            triggerType: workflow.definition.trigger.type,
                            configuration: workflow.definition.trigger.configuration,
                        },
                        position: { x: 250, y: 50 },
                    },
                    ...(workflow.definition?.actions?.map((action, idx) => ({
                        id: `action-${idx}`,
                        type: 'action',
                        data: {
                            label: `Then ${action.type}`,
                            actionType: action.type,
                            configuration: action.configuration,
                        },
                        position: { x: 250, y: 150 + idx * 100 },
                    })) || []),
                ]
                : [];
            const edges = nodes
                .slice(0, -1)
                .map((node, idx) => ({
                id: `edge-${idx}`,
                source: node.id,
                target: nodes[idx + 1].id,
                animated: true,
            }));
            set({
                canvasNodes: nodes,
                canvasEdges: edges,
                isPublished: workflow.is_published,
            });
        }
    },
    setCanvasNodes: (nodes) => set({ canvasNodes: nodes }),
    setCanvasEdges: (edges) => set({ canvasEdges: edges }),
    addNode: (node) => set((state) => ({
        canvasNodes: [...state.canvasNodes, node],
    })),
    removeNode: (nodeId) => set((state) => {
        const nodes = state.canvasNodes.filter((n) => n.id !== nodeId);
        const edges = state.canvasEdges.filter((e) => e.source !== nodeId && e.target !== nodeId);
        return { canvasNodes: nodes, canvasEdges: edges };
    }),
    updateNode: (nodeId, updates) => set((state) => ({
        canvasNodes: state.canvasNodes.map((node) => node.id === nodeId ? { ...node, ...updates } : node),
    })),
    addEdge: (edge) => set((state) => ({
        canvasEdges: [...state.canvasEdges, edge],
    })),
    removeEdge: (edgeId) => set((state) => ({
        canvasEdges: state.canvasEdges.filter((e) => e.id !== edgeId),
    })),
    setIsPublished: (published) => set({ isPublished: published }),
    setExecutionHistory: (history) => set({ executionHistory: history }),
    setCanvasState: (state) => set((prevState) => ({
        canvasState: { ...prevState.canvasState, ...state },
    })),
    setLoading: (loading) => set({ loading }),
    setError: (error) => set({ error }),
    resetCanvas: () => set({
        canvasNodes: [],
        canvasEdges: [],
        activeWorkflow: null,
        isPublished: false,
        canvasState: {
            zoom: 1,
            pan: { x: 0, y: 0 },
        },
    }),
}));
